import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'antd';
import { Check, Clipboard, Terminal } from 'lucide-react';
import './OpenClawProductHero.css';

/**
 * OpenClawProductHero — §2.
 *
 * Two-panel composition:
 *   Left: animated terminal that auto-types a Python SDK call and shows
 *         the streaming response. Triggered by IntersectionObserver (runs
 *         when the section enters the viewport).
 *   Right: static "Copy and try" code block with a copy-button that flashes
 *          "Copied ✓" for 2s (industry-standard pattern, same as ApiKeyManager).
 *
 * Developers like seeing the code move; they LOVE being able to run it.
 * Animation is the catch; the static copy block is the conversion.
 */
const OpenClawProductHero = ({ onGetApiKey, ctaLabel }) => {
  const rootRef = useRef(null);
  const [terminalStarted, setTerminalStarted] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyResetTimer = useRef(null);

  // Start the animation when the section scrolls into view.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTerminalStarted(true);
            io.disconnect(); // one-shot animation, don't re-trigger on scroll
          }
        });
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Clean up the copy-reset timeout if the component unmounts mid-flash.
  useEffect(() => {
    return () => {
      if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
    };
  }, []);

  const codeSnippet = `from cellcog import Client

client = Client(agent_provider="openclaw")
# reads CELLCOG_API_KEY from env

result = client.create_chat(
    prompt="Research quantum computing trends and generate a 2-page PDF",
    notify_session_key="agent:main:main",
    task_label="research",
)

print(result["message"])
# → "Research started. I'll notify you when the PDF is ready."`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeSnippet);
      setCopied(true);
      if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
      copyResetTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silent fail — user can select + copy manually
    }
  };

  return (
    <section className="openclaw-product-hero" ref={rootRef}>
      <div className="section">
        <div className="section-eyebrow">See it in action</div>
        <h2 className="section-title display-font">
          One API key. Every modality. Zero framework lock-in.
        </h2>
        <p className="section-subtitle">
          Any OpenClaw agent can delegate to CellCog with three lines of
          Python. The SDK returns a unified response your agent narrates
          back — no streaming plumbing, no tool-specific wiring.
        </p>

        <div className="openclaw-product-hero__split">
          {/* LEFT: animated terminal */}
          <div className="openclaw-product-hero__terminal" aria-hidden="true">
            <div className="openclaw-terminal-chrome">
              <span className="openclaw-terminal-dot openclaw-terminal-dot--red" />
              <span className="openclaw-terminal-dot openclaw-terminal-dot--yellow" />
              <span className="openclaw-terminal-dot openclaw-terminal-dot--green" />
              <span className="openclaw-terminal-chrome-label">
                my_agent.py — python3
              </span>
            </div>
            <div className="openclaw-terminal-body">
              <AnimatedCode started={terminalStarted} />
            </div>
          </div>

          {/* RIGHT: copy-and-try block */}
          <div className="openclaw-product-hero__copy">
            <div className="openclaw-copy-card">
              <div className="openclaw-copy-card__header">
                <Terminal size={16} className="openclaw-copy-card__icon" />
                <span className="openclaw-copy-card__label">
                  Python SDK
                </span>
                <Button
                  size="small"
                  className={`openclaw-copy-card__btn${copied ? ' openclaw-copy-card__btn--success' : ''}`}
                  icon={copied ? <Check size={13} /> : <Clipboard size={13} />}
                  onClick={handleCopy}
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <pre className="openclaw-copy-card__code">
                <code>{codeSnippet}</code>
              </pre>
              <div className="openclaw-copy-card__footer">
                <span className="openclaw-copy-card__hint">
                  Install first with{' '}
                  <code>openclaw skills install @cellcog/cellcog</code> or{' '}
                  <code>openclaw plugins install clawhub:@cellcog/openclaw-plugin</code>
                </span>
              </div>
            </div>

            <Button
              type="primary"
              size="large"
              className="openclaw-product-hero__cta"
              onClick={onGetApiKey}
              data-msc-suppress
            >
              {ctaLabel}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * AnimatedCode — lightweight typewriter effect for the terminal.
 *
 * Auto-types the Python snippet line-by-line, then appends a fake streaming
 * "response" section showing CellCog's reply. Fully CSS-timed via setState
 * intervals; no animation library dependency.
 *
 * `prefers-reduced-motion` is respected — we render the full content
 * immediately for users who've opted out.
 */
const CODE_LINES = [
  { text: 'from cellcog import Client', className: 'kw' },
  { text: '', className: '' },
  { text: 'client = Client(agent_provider="openclaw")', className: 'code' },
  { text: '', className: '' },
  { text: 'result = client.create_chat(', className: 'code' },
  { text: '    prompt="Research quantum computing trends…",', className: 'str' },
  { text: '    notify_session_key="agent:main:main",', className: 'str' },
  { text: '    task_label="research",', className: 'str' },
  { text: ')', className: 'code' },
  { text: '', className: '' },
  { text: 'print(result["message"])', className: 'code' },
];

const STREAM_LINES = [
  { text: '▸ Research started.', className: 'stream-info' },
  { text: '  Session: agent:main:main', className: 'stream-muted' },
  { text: '  Task: research', className: 'stream-muted' },
  { text: '▸ Notifying when the PDF is ready.', className: 'stream-ok' },
];

const AnimatedCode = ({ started }) => {
  const [visibleLines, setVisibleLines] = useState(0);
  const [streamVisibleLines, setStreamVisibleLines] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const onChange = (e) => setReducedMotion(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    if (!started) return undefined;
    if (reducedMotion) {
      setVisibleLines(CODE_LINES.length);
      setStreamVisibleLines(STREAM_LINES.length);
      return undefined;
    }

    let cancelled = false;
    const tickCode = async () => {
      for (let i = 1; i <= CODE_LINES.length; i += 1) {
        if (cancelled) return;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 120));
        setVisibleLines(i);
      }
      await new Promise((r) => setTimeout(r, 400));
      for (let i = 1; i <= STREAM_LINES.length; i += 1) {
        if (cancelled) return;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 320));
        setStreamVisibleLines(i);
      }
    };
    tickCode();
    return () => {
      cancelled = true;
    };
  }, [started, reducedMotion]);

  return (
    <div className="openclaw-terminal-code">
      {CODE_LINES.slice(0, visibleLines).map((line, idx) => (
        <div
          key={`c-${idx}`}
          className={`openclaw-terminal-line openclaw-terminal-line--${line.className || 'code'}`}
        >
          {line.text || '\u00A0'}
        </div>
      ))}
      {streamVisibleLines > 0 && (
        <div className="openclaw-terminal-separator" aria-hidden="true" />
      )}
      {STREAM_LINES.slice(0, streamVisibleLines).map((line, idx) => (
        <div
          key={`s-${idx}`}
          className={`openclaw-terminal-line openclaw-terminal-line--${line.className}`}
        >
          {line.text}
        </div>
      ))}
    </div>
  );
};

export default OpenClawProductHero;
